import { fabric } from 'fabric'
import { v4 as guid } from 'uuid'
import type { Subscription } from 'dexie'
import { liveQuery } from 'dexie'
import { clone, debounce, isEmpty, pick } from 'lodash-es'
import Utility from '../services/utility'
import type WbDynamic from './dynamic'
import WbDesignOption from './designOption'
import MyArticle from '@/models/myArticle'
import type Article from '@/models/article'
import type DuneAsset from '@/models/duneAsset'
import pngLoading from '@/assets/loading.png'
import pngNoImage from '@/assets/noimg.png'
import appConfig from '@/services/appConfig'
import { useUserStore } from '@/store/userData'
import utils from '@/services/utils'
import { appConstants, privileges, whiteboardConstants } from '@/models/constants'
import { getArticleAssets, getArticlesStream } from '@/api/t1/article'
import type { ArticleFilterModel } from '@/api/t1/model/articleModel'
import { FilterCriteriaMode } from '@/models/filterCriteria'
import type Resource from '@/models/resource'
import { getDesignOptionAssets } from '@/api/dune/asset'
import promiseQueue from '@/services/promiseQueue'

interface IWbArticleImageOptions extends fabric.IImageOptions, IWbObjectProps {
  id?: string
  catalogCode: number
  articleId: number
  objectId: string
  isRequest?: boolean
  assetKey?: string
  lock?: boolean
  preventUnlock?: boolean
  requestWaterMarkDetails?: Record<string, any>
  colorCode?: string
  status?: boolean
  localOnly?: boolean
  attributes?: string[]
  attributesSeparator?: string
  attributePlaceHolder?: string
  noImageObjectProperties?: Record<string, any>
  article?: MyArticle
  showResources?: boolean
  showDesignOptions?: boolean
  preventDelete?: boolean
  excludeFromGroupSelection?: boolean
  favColorList?: string[]
  dynamicGrid?: WbDynamic
  resourceData?: IResourcePosition[]
  designOptionData?: IDesignOptionPosition[]
  articleWaterMarkDetails?: Record<string, any>
  icons?: string[]
}
export default class WbArticleImage extends fabric.Image implements IWbObject {
  public id: string
  public catalogCode: number
  public articleId: number
  public objectId: string
  public isRequest: boolean
  public requestWaterMarkDetails: Record<string, any> = {}
  public colorCode = ''
  public status?: boolean
  public articleNumber?: string
  public blockImage?: number
  public assetKey?: string
  public assets: DuneAsset[]
  public designOptions: DuneAsset[]
  public type = whiteboardConstants.objectTypes.articleImage
  public selected = false
  public highlighted = false
  public lock: boolean
  public connectable: boolean
  public imgLoaded: boolean
  public imgLoading: boolean
  public localOnly: boolean
  public preventUnlock: boolean
  private isLoadingFirstTime = true
  public article?: MyArticle
  public attributes?: string[]
  public attributesSeparator?: string
  public attributePlaceHolder?: string
  public cachedAttributeThumbnailValue: string | null = null
  public noImageObjectProperties: { backgroundColor?: string, border?: { borderColor: string, strokeWidth: number }, attributes?: string[] } = {}
  public showResources: boolean
  public showDesignOptions: boolean
  public resourceData: IResourcePosition[] = []
  public designOptionData: IDesignOptionPosition[] = []
  private resourceObjects: fabric.Object[] = []
  private designOptionsObjects: fabric.Object[] = []
  public preventDelete?: boolean
  public canBeLinkedTo = true
  public canLink = false
  public _isMoving = false
  private displayedImg: 'loading' | 'noImage' | 'image' | 'articleDetails' | 'none' = 'none'
  public excludeFromGroupSelection?: boolean
  public favColorList: string[] = []
  public dynamicGrid?: WbDynamic
  public link = undefined
  private validImageResources: Resource[] = []
  private isResourcesImagesRendered: boolean
  public articleWaterMarkDetails: Record<string, any> = {}
  public isNewArticle = false
  private newArticleTimeout: number | null = null
  public icons: string[] = []

  public editableProps: Record<string, IWbObjectProp> = {
    imageType: { name: 'Image Type', type: 'imageType', editable: true },
    scale: { name: 'Size', type: 'scale', editable: true },
    lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    addArticleDetails: { name: 'Add Article Details', type: 'addArticleDetails', editable: true },
    addDiscussion: { name: 'Add Comment', type: 'addDiscussion', editable: true, editableWhenLocked: true },
    assignImage: { name: 'Assign Image', type: 'assignImage', editable: true, editableWhenLocked: true },
    deleteImage: { name: 'Delete Image', type: 'deleteImage', editable: true, editableWhenLocked: true },
    showResources: { name: 'Show Resources', type: 'showResources', editable: true, editableWhenLocked: true },
    showDesignOptions: { name: 'Show Design Options', type: 'showDesignOptions', editable: true, editableWhenLocked: true },
    addThumbnailAttributes: { name: 'Add Thumbnail Attributes', type: 'addThumbnailAttributes', editable: false },
    backgroundColor: { name: 'Background Color', type: 'backgroundColor', editable: false },
    border: { name: 'Border', type: 'border', editable: false },
    icons: { name: 'Add Icons', type: 'icons', editable: true, editableWhenLocked: true },
    info: { name: 'Info', type: 'info', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    // group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    copyModel: {
      action: 'copyModel',
      label: 'Copy Model',
      faicon: 'fa-light fa-copy',
      showInSubMenu: (() => {
        const userStore = useUserStore()
        return userStore.activeCatalog?.DataSourceTypeId !== appConstants.catalogTypes.inherited
          && !userStore.currentCustomer
          && userStore.userProfile.isValidPrivilege(privileges.article.carryOverArticles)
          && userStore.userProfile.isValidPrivilege(privileges.article.createModelWithArticles)
          && (userStore.userProfile.AccountDetails.AccountTypeId === 1 || userStore.userProfile.AccountDetails.AccountId === userStore.activeCatalog?.AccountId)
      })(),
    },
    copyArticles: {
      action: 'copyArticles',
      label: 'Copy Articles',
      faicon: 'fa-light fa-copy',
      showInSubMenu: (() => {
        const userStore = useUserStore()
        return this.catalogCode === userStore.activeCatalog?.CatalogCode
          && userStore.activeCatalog?.DataSourceTypeId !== appConstants.catalogTypes.inherited
          && !userStore.currentCustomer
          && userStore.userProfile.isValidPrivilege(privileges.article.addOrCarryoverArticles)
          && (userStore.userProfile.AccountDetails.AccountTypeId === 1 || userStore.userProfile.AccountDetails.AccountId === userStore.activeCatalog?.AccountId)
      })(),
    },
    editArticles: {
      action: 'editArticles',
      label: 'Edit Articles',
      faicon: 'fa-light fa-edit',
      showInSubMenu: (() => {
        const userStore = useUserStore()
        return this.catalogCode === userStore.activeCatalog?.CatalogCode && userStore.userProfile.isValidPrivilege(privileges.article.update)
          && (userStore.userProfile.AccountDetails.AccountTypeId === 1 || userStore.userProfile.AccountDetails.AccountId === userStore.activeCatalog?.AccountId)
      })(),
    },
    cloneAsImage: { action: 'cloneAsImage', label: 'Copy as Image', faicon: 'fa-light fa-clone', showInSubMenu: true },
    saveImages: {
      action: 'saveImages',
      label: 'Save Images',
      faicon: 'fa-light fa-download',
      showInSubMenu: (() => {
        const userStore = useUserStore()
        return userStore.activeCatalog?.Config.AllowDownloadImages === true && !this.isRequest
      })(),
    },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
    editFavorites: { action: 'editFavorites', label: 'Edit Favorites', faicon: 'fa-light fa-star', showInSubMenu: true },
  }

  public subscriptions: Array<Subscription>

  constructor(element: string | HTMLImageElement | HTMLCanvasElement | HTMLVideoElement, options: IWbArticleImageOptions, isLiveObject = true) {
    super(element, options)
    const userStore = useUserStore()
    this.id = options.id || guid()
    this.catalogCode = options.catalogCode
    this.articleId = options.articleId
    this.objectId = options.objectId
    this.isRequest = options.isRequest || false
    this.article = options.article
    this.requestWaterMarkDetails = options.requestWaterMarkDetails || { fontColor: '#5093e16e', waterMarkText: 'Request' }
    this.colorCode = options.colorCode || ''
    this.status = options.status
    this.assetKey = options.assetKey
    this.lock = options.lock || false
    this.localOnly = options.localOnly || false
    this.connectable = true
    this.assets = []
    this.designOptions = []
    this.imgLoaded = false
    this.imgLoading = false
    this.subscriptions = []
    this.preventUnlock = options.preventUnlock || false
    this.attributesSeparator = options?.attributesSeparator || '\n'
    this.attributePlaceHolder = options?.attributePlaceHolder || ''
    this.showResources = Boolean(options?.showResources)
    this.showDesignOptions = utils.isDefined(options?.showDesignOptions) ? Boolean(options?.showDesignOptions) : userStore.activeCatalog && userStore.userPreferences && userStore.userPreferences.whiteboard && userStore.userPreferences.whiteboard.settings && userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode] && userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showArticleDesignOptions ? userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showArticleDesignOptions : false
    this.dynamicGrid = options.dynamicGrid
    this.resourceObjects = []
    this.designOptionsObjects = []
    this.resourceData = options?.resourceData || []
    this.designOptionData = options?.designOptionData || []
    this.objectCaching = false // we dont want to cache this object
    this.isResourcesImagesRendered = true
    this.articleWaterMarkDetails = options.articleWaterMarkDetails || {}
    this.icons = options.icons || []

    this.preventDelete = options.preventDelete || false
    this.excludeFromGroupSelection = options.excludeFromGroupSelection || false
    this.favColorList = options.favColorList || []
    const resourceType = userStore.activeCatalog?.Config.WhiteboardDefaultResourceType
    if (userStore.activeCatalog?.Config.ShowWhiteboardThumbnailAttributesIfNoImage) {
      if (options.noImageObjectProperties && options.noImageObjectProperties.attributes) {
        this.noImageObjectProperties = options.noImageObjectProperties
        this.applyNoImageProperties()
      }
      else {
        this.backgroundColor = '#ffffff'
        this.setProp('border', { borderColor: '#000000', strokeWidth: 1 }, true, false)
        this.noImageObjectProperties.attributes = options?.attributes?.length ? options.attributes : userStore.activeCatalog?.Config.WhiteboardThumbnailAttributes.length ? userStore.activeCatalog?.Config.WhiteboardThumbnailAttributes : ['ArticleNumber', 'ModelName'] // default are ArticleNumber and ModelName // assign default
        this.attributes = this.noImageObjectProperties.attributes
      }
    }
    if (options.article && options.article instanceof MyArticle) {
      this.loadTextValue(options.article)
    }
    // check if article is new
    this.checkAndMarkAsNew()
    if (isLiveObject) {
      const catalogDetails = userStore.linkedCatalogDetails[this.catalogCode] || userStore.activeCatalog
      if (this.isRequest) {
        const observable = liveQuery(async () => await appConfig.DB!.requests.where({ CatalogCode: catalogDetails.CatalogCode, Id: this.articleId }).toArray())
        const subscription = observable.subscribe(async ([request]) => {
          const article = await utils.getRequestArticle(catalogDetails, request, appConfig.DB, userStore.myAttributes, userStore.priceGroups)
          // As discussed with Andre we dont need to set article again when we first time create the object as already we are loading the article
          if (!this.isLoadingFirstTime) {
            this.articleNumber = article.ArticleNumber
            this.setProp('status', article.Status === 1, true)
            this.canvas?.requestRenderAll()
          }
          else {
            this.isLoadingFirstTime = false
          }
        })
        this.subscriptions.push(subscription)
      }
      else {
        // // non segmented article will have _IsNonSegmented as true
        // const articleIds = [this.articleId]
        // const observable = liveQuery(async () => await appConfig.DB!.getMyArticles(userStore.activeCatalog!, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, articleIds, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, true, userStore.currentCustomer, userStore.currentCustomerSegmentations, true))
        // const subscription = observable.subscribe(async ([article]) => {
        //   // As discussed with Andre we dont need to set article again when we first time create the object as already we are loading the article
        //   // this.article = article
        //   if (!this.isLoadingFirstTime) {
        //     this.loadTextValue(article)
        //     this.articleNumber = article.ArticleNumber
        //     if (this.blockImage !== article.BlockImage) {
        //       this.imgLoaded = false
        //       this.imgLoading = false
        //     }
        //     this.blockImage = article.BlockImage
        //     this.setProp('status', article.Status === 1, true)
        //     this.setProp('colorCode', '', true)
        //     if (userStore.activeCatalog?.Config.WhiteboardColorCodingAttribute && userStore.activeCatalog.Config.WhiteboardColorCodingAttribute?.length !== 0 && userStore.activeCatalog.Config.WhiteboardColorCodingAttribute[0].attribute) {
        //       for (const configValue of userStore.activeCatalog.Config.WhiteboardColorCodingAttribute) {
        //         if (utils.isValidStringValue(article[configValue.attribute]) && article[configValue.attribute]?.toString().trim().toLowerCase() === configValue.value.toString().toLowerCase()) {
        //           this.setProp('colorCode', configValue.color, true)
        //           break
        //         }
        //       }
        //     }
        //     this.canvas?.requestRenderAll()
        //   }
        //   else {
        //     this.isLoadingFirstTime = false
        //   }
        // })
        // this.subscriptions.push(subscription)

        // resources
        const resourcesSubscription = liveQuery(async () =>
          await appConfig.DB!.resources.where('Articles')
            .anyOf([this.articleId])
            .filter(r => r.CatalogCode === catalogDetails.CatalogCode)
            .toArray(),
        ).subscribe((resources) => {
          this.validImageResources = resources.filter(r =>
            r.ResourceType.toLowerCase().includes(resourceType!.toLowerCase())
            && r.Status === 1,
          )
          // remove deleted resourse data
          const validIds = new Set(this.validImageResources.map(r => r.ResourceId))
          this.resourceData = this.resourceData.filter(r => validIds.has(r.id))

          if (this.showResources) { // render if showresource
            this.createResourceObjects(this.validImageResources)
          }
          // if no resources then set the show resources false
          if (!this.validImageResources.length && this.showResources) {
            this.resourceData = []
            this.setProp('showResources', { showResources: false }, true)
          }
        })

        this.subscriptions.push(resourcesSubscription)
        if (userStore.activeCatalog && ((!utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode]) || !utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showFavorites)) || userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showFavorites)) {
          const favoritesObservable = liveQuery(async () => await appConfig.DB!.myFavorites.where('[CatalogCode+UserId+CreatedByUserName+Status+CustomerId]').equals([userStore.activeCatalog!.CatalogCode, userStore.userProfile.Id, userStore.currentUsername, 1, userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1]).toArray())
          const favSubscription = favoritesObservable.subscribe(async (favoritesResponse) => {
            this.favColorList = []
            if (favoritesResponse != null && favoritesResponse.length > 0) {
              this.favColorList = favoritesResponse.filter(fav => fav.Articles.includes(this.articleId)).map(itm => itm.Color)
            }
            this.canvas?.requestRenderAll()
          })
          this.subscriptions.push(favSubscription)
        }
      }
    }

    this.setLock(this.lock)

    this.stateProperties = this.stateProperties?.concat(['showResources', 'lock'])

    this.on('added', () => {
      if (this.validImageResources.length > 0 && !this.isResourcesImagesRendered) {
        this.createResourceObjects(this.validImageResources)
      }
      this.handleDesignOptionsVisibility(this.showDesignOptions)
    })

    this.on('selected', () => {
      this.selected = true
    })

    this.on('deselected', () => {
      this.selected = false
    })

    this.on('removed', () => {
      this.cleanupNewArticleTimeouts()
      if (this.resourceObjects.length > 0) {
        this.canvas?.remove(...this.resourceObjects)
      }
      if (this.designOptionsObjects.length > 0) {
        this.canvas?.remove(...this.designOptionsObjects)
      }

      this.subscriptions.forEach((subscription) => {
        if (subscription && !subscription.closed) {
          subscription.unsubscribe()
        }
      })
    })

    this.on('moving', () => {
      this._isMoving = true
    })

    this.on('mouseup', () => {
      this._isMoving = false
    })

    this.on('scaling', () => {
      this.updateLinkWhenUpdated()
      this.canvas?.fire('object:modified', { target: this })

      this.canvas?.requestRenderAll()
    })
  }

  private async createDesignOptionObjects(designOptions: DuneAsset[]) {
    this.canvas?.remove(...this.designOptionsObjects)
    this.designOptionsObjects = []

    const horizontalGap = 20 // first time
    const verticalStartGap = 50 // first time
    const gapBetweenDesignOptions = 10 // first time

    const parentMatrix = this.calcTransformMatrix()
    const parentCoords = fabric.util.qrDecompose(parentMatrix)
    const invParentMatrix = fabric.util.invertTransform(parentMatrix)

    const scaledW = (this.width || 0) * (this.scaleX || 1)
    const scaledH = (this.height || 0) * (this.scaleY || 1)

    const baseLeft = parentCoords.translateX + scaledW / 2 + horizontalGap
    let currentTop = parentCoords.translateY - scaledH / 2 + verticalStartGap
    const existingDesignOptions: DuneAsset[] = []
    const nonExistingDesignOptions: DuneAsset[] = []
    for (const designOption of designOptions) {
      const position = this.designOptionData.find(p => p.id === designOption.Id)
      if (position) {
        existingDesignOptions.push(designOption)
      }
      else {
        nonExistingDesignOptions.push(designOption)
      }
    }
    let totalExistingHeight = 0

    // --- existing options ---
    for (const designOption of existingDesignOptions) {
      const position = this.designOptionData.find(p => p.id === designOption.Id)!
      const currentHeightAndWidth = WbDesignOption.getDesignOptionHeightWidth(designOption.Width, designOption.Height, position.scale)

      const maxActualSize = Math.max(designOption.Width, designOption.Height)
      const aspectRatio = designOption.Width / designOption.Height
      const currentMaxSize = Math.max(currentHeightAndWidth.scaledWidth, currentHeightAndWidth.scaledHeight)
      const newResolution = WbDesignOption.getNearestResolution(currentMaxSize, maxActualSize)

      const width = designOption.Height > designOption.Width
        ? Math.round(newResolution / (currentHeightAndWidth.scaledHeight / currentHeightAndWidth.scaledWidth))
        : newResolution
      const height = designOption.Height > designOption.Width
        ? newResolution
        : Math.round(newResolution / aspectRatio)

      const scaleX = currentHeightAndWidth.scaledWidth / width
      const scaleY = currentHeightAndWidth.scaledHeight / height

      //  compute world position from stored local link
      const worldPt = fabric.util.transformPoint(
        new fabric.Point(position.link.x, position.link.y),
        parentMatrix,
      )

      const options = {
        article: this.article,
        assetId: designOption.Id,
        asset: designOption,
        articleId: this.articleId,
        assetStorageFile: designOption.StorageFile,
        top: worldPt.y,
        left: worldPt.x,
        scaleX,
        scaleY,
      }

      const designOptionObject = await WbDesignOption.loadFromUrl(options, width, height) as WbDesignOption
      totalExistingHeight += currentHeightAndWidth.scaledHeight + gapBetweenDesignOptions
      this.setDesignOptionEventHandlers(designOptionObject, designOption.Id)
      //  recompute link back into local coords
      const childCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
      const childWorld = new fabric.Point(childCoords.translateX, childCoords.translateY)
      const childLocal = fabric.util.transformPoint(childWorld, invParentMatrix)
      designOptionObject.link = { id: this.id, x: childLocal.x, y: childLocal.y } as any
      this.designOptionsObjects.push(designOptionObject)
      this.canvas?.add(designOptionObject)
    }

    currentTop += totalExistingHeight
    // --- new options ---
    for (const designOption of nonExistingDesignOptions) {
    //  convert new world coords → local
      const localPt = fabric.util.transformPoint(
        new fabric.Point(baseLeft, currentTop),
        invParentMatrix,
      )

      const position = {
        id: designOption.Id,
        link: { x: localPt.x, y: localPt.y },
        scale: WbDesignOption.getDesignOptionScale(designOption.Width, designOption.Height),
      }
      this.designOptionData.push(position)

      //  convert back local → world for placement
      const worldPt = fabric.util.transformPoint(
        new fabric.Point(localPt.x, localPt.y),
        parentMatrix,
      )

      const currentData = WbDesignOption.getDesignOptionHeightWidth(designOption.Width, designOption.Height, position.scale)
      const options = { article: this.article, assetId: designOption.Id, asset: designOption, articleId: this.articleId, assetStorageFile: designOption.StorageFile, top: worldPt.y, left: worldPt.x }
      const designOptionObject = await WbDesignOption.loadFromUrl(options, currentData.scaledWidth, currentData.scaledHeight) as WbDesignOption
      this.setDesignOptionEventHandlers(designOptionObject, designOption.Id)

      //  store link back in local coords
      const childCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
      const childWorld = new fabric.Point(childCoords.translateX, childCoords.translateY)
      const childLocal = fabric.util.transformPoint(childWorld, invParentMatrix)

      designOptionObject.link = { id: this.id, x: childLocal.x, y: childLocal.y } as any

      this.designOptionsObjects.push(designOptionObject)
      this.canvas?.add(designOptionObject)
      currentTop += designOptionObject.height! + gapBetweenDesignOptions
    }
    if (nonExistingDesignOptions.length > 0) {
      this.canvas?.fire('object:modified', { target: this })
    }
    this.canvas?.requestRenderAll()
  }

  private async createResourceObjects(resources: Resource[]) {
    if (!utils.isDefined(this.canvas)) {
      this.isResourcesImagesRendered = false
      return
    }
    else {
      this.isResourcesImagesRendered = true
    }

    this.canvas?.remove(...this.resourceObjects)
    this.resourceObjects = []

    const horizontalGap = 10 // first time
    const verticalStartGap = 50 // first time
    const gapBetweenResources = 10 // first time
    const resourceSize = 90 // first time
    const parentCoords = fabric.util.qrDecompose(this.calcTransformMatrix())

    const baseLeft = (this.left || 0) - (this.width || 0) * (this.scaleX || 1) / 2 - horizontalGap - resourceSize
    let currentTop = (this.top || 0) - (this.height || 0) * (this.scaleY || 1) / 2 + verticalStartGap

    let totalExistingHeight = 0
    const existingResources: Resource[] = []
    const newResources: Resource[] = []

    for (const resource of resources) {
      const position = this.resourceData.find(p => p.id === resource.ResourceId)
      if (position) {
        existingResources.push(resource)
      }
      else {
        newResources.push(resource)
      }
    }

    // render existing resources and calculate the total height to find out where to render newly added resources
    for (const resource of existingResources) {
      const resourceId = resource.ResourceId
      const position = this.resourceData.find(p => p.id === resourceId)!

      await new Promise<void>((resolve) => {
        const htmlImg = new Image()
        htmlImg.crossOrigin = 'anonymous'
        htmlImg.src = resource.ResourceUrl
        htmlImg.onload = () => {
          totalExistingHeight += htmlImg.height * position.scale + gapBetweenResources
          const fabricImg = new fabric.Image(htmlImg, {
            left: parentCoords.translateX + position.link.x * this.scaleX!,
            top: parentCoords.translateY + position.link.y * this.scaleY!,
            scaleX: position.scale,
            scaleY: position.scale,
            selectable: true,
            visible: this.showResources,
            evented: true,
            originX: 'top',
            originY: 'left',
            data: {
              isResource: true,
              canLink: true,
              resourceId,
            },
          })
          fabricImg.set({
            preventDelete: true,
          } as unknown as Partial<this>)
          this.setupResourceEventHandlers(fabricImg, resourceId)

          const imgCoords = fabric.util.qrDecompose(fabricImg.calcTransformMatrix())
          this.setCoords()
          fabricImg.data.link = {
            id: this.id,
            x: imgCoords.translateX - parentCoords.translateX,
            y: imgCoords.translateY - parentCoords.translateY,
          }

          this.resourceObjects.push(fabricImg)
          this.canvas?.add(fabricImg)
          resolve()
        }
      })
    }

    currentTop += totalExistingHeight
    // render newly added resource
    for (const resource of newResources) {
      const resourceId = resource.ResourceId

      await new Promise<void>((resolve) => {
        const htmlImg = new Image()
        htmlImg.crossOrigin = 'anonymous'
        htmlImg.src = resource.ResourceUrl
        htmlImg.onload = () => {
          const maxSize = 150
          const width = htmlImg.width
          const height = htmlImg.height
          let scaleX = 1
          let scaleY = 1

          if (width > height) {
            if (width > maxSize) {
              const newHeight = height * (maxSize / width)
              scaleX = maxSize / width
              scaleY = newHeight / height
            }
          }
          else {
            if (height > maxSize) {
              const newWidth = width * (maxSize / height)
              scaleX = newWidth / width
              scaleY = maxSize / height
            }
          }

          const scale = Math.min(scaleX, scaleY)
          const linkX = ((baseLeft - (this.left || 0)) / this.scaleX!)
          const linkY = ((currentTop - (this.top || 0)) / this.scaleY!)

          const position = {
            id: resourceId,
            scale,
            link: { x: linkX, y: linkY },
          }
          this.resourceData.push(position)

          const fabricImg = new fabric.Image(htmlImg, {
            left: parentCoords.translateX + position.link.x * this.scaleX!,
            top: parentCoords.translateY + position.link.y * this.scaleY!,
            scaleX: position!.scale,
            scaleY: position!.scale,
            selectable: true,
            visible: this.showResources,
            evented: true,
            originX: 'top',
            originY: 'left',
            data: {
              isResource: true,
              canLink: true,
              resourceId,
            },
          })

          this.setupResourceEventHandlers(fabricImg, resourceId)

          const imgCoords = fabric.util.qrDecompose(fabricImg.calcTransformMatrix())
          this.setCoords()
          fabricImg.data.link = {
            id: this.id,
            x: imgCoords.translateX - parentCoords.translateX,
            y: imgCoords.translateY - parentCoords.translateY,
          }

          this.resourceObjects.push(fabricImg)
          this.canvas?.add(fabricImg)

          currentTop += (htmlImg.height * position!.scale) + gapBetweenResources
          resolve()
        }
      })
    }
    if (newResources.length > 0) {
      this.canvas?.fire('object:modified', { target: this })
    }
    this.canvas?.requestRenderAll()
  }

  private handleDesignOptionsVisibility(visible: boolean) {
    const userStore = useUserStore()
    if (visible) {
      //
      const catalogDetails = userStore.activeCatalog
      if (catalogDetails && this.article) {
        getDesignOptionAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, this.article.ArticleNumber, false, whiteboardConstants.abortController.signal)
          .then((assets) => {
            this.designOptions = assets
            if (this.designOptions.length > 0) {
              this.createDesignOptionObjects(this.designOptions)
              // remove deleted design options data
              const validIds = new Set(this.designOptions.map(r => r.Id))
              this.designOptionData = this.designOptionData.filter(r => validIds.has(r.id))
            }
            else if (this.showDesignOptions) { // if no design options then set the show design options false
              this.designOptionData = []
              // this.setProp('showDesignOptions', { showDesignOptions: false }, true)
            }
          }).catch((_e) => {
            if (this.designOptions.length > 0) {
              this.createDesignOptionObjects(this.designOptions)
              // remove deleted design options data
              const validIds = new Set(this.designOptions.map(r => r.Id))
              this.designOptionData = this.designOptionData.filter(r => validIds.has(r.id))
            }
            else if (this.showDesignOptions) { // if no design options then set the show design options false
              this.designOptionData = []
              // this.setProp('showDesignOptions', { showDesignOptions: false }, true)
            }
          })
      }
    }
    else {
      this.designOptionsObjects.forEach((obj) => {
        if (this.canvas?.contains(obj)) {
          this.canvas?.remove(obj)
        }
      })
      this.designOptionsObjects = []
      this.canvas?.requestRenderAll()
    }
  }

  private handleResourcesVisibility(visible: boolean) {
    if (visible) {
      if (this.validImageResources.length > 0) {
        this.createResourceObjects(this.validImageResources)
      }
    }
    else {
      this.resourceObjects.forEach((obj) => {
        if (this.canvas?.contains(obj)) {
          this.canvas?.remove(obj)
        }
      })
      this.resourceObjects = []
      this.canvas?.requestRenderAll()
    }
  }

  updateLinkWhenUpdated() {
    const articleCoords = fabric.util.qrDecompose(this.calcTransformMatrix())

    this.resourceObjects.forEach((resourceObj) => {
      const resourceId = resourceObj.data?.resourceId
      if (resourceId) {
        const pos = this.resourceData.find(p => p.id === resourceId)
        if (pos) {
          pos.scale = resourceObj.scaleX || 0.3

          const resourceCoords = fabric.util.qrDecompose(resourceObj.calcTransformMatrix())
          pos.link = {
            id: this.id,
            x: (resourceCoords.translateX - articleCoords.translateX) / this.scaleX!,
            y: (resourceCoords.translateY - articleCoords.translateY) / this.scaleY!,
          }
        }
      }
    })

    this.designOptionsObjects.forEach((designObject) => {
      const designOptionObject = designObject as WbDesignOption
      const assetId = designOptionObject.assetId
      if (assetId) {
        const pos = this.designOptionData.find(p => p.id === assetId)
        if (!pos) { return }
        const designObjectCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
        // Convert from center-origin to top-left
        const topLeftX = designObjectCoords.translateX - (designOptionObject.width! * designOptionObject.scaleX!) / 2
        const topLeftY = designObjectCoords.translateY - (designOptionObject.height! * designOptionObject.scaleY!) / 2

        pos.link = {
          x: (topLeftX - articleCoords.translateX) / this.scaleX!,
          y: (topLeftY - articleCoords.translateY) / this.scaleY!,
        }
      }
    })
  }

  onServerUpdate() {
    if (this._isMoving) {
      this.updateLinkWhenUpdated()
    }
    this.canvas?.requestRenderAll()
  }

  private setupResourceEventHandlers(resourceObj: fabric.Object, resourceId: number) {
    resourceObj.on('modified', () => {
      const articleCoords = fabric.util.qrDecompose(this.calcTransformMatrix())
      const pos = this.resourceData.find(p => p.id === resourceId)
      if (!pos) { return }

      pos.scale = resourceObj.scaleX || 0.3

      const resourceCoords = fabric.util.qrDecompose(resourceObj.calcTransformMatrix())

      pos.link = {
        id: this.id,
        x: (resourceCoords.translateX - articleCoords.translateX) / this.scaleX!,
        y: (resourceCoords.translateY - articleCoords.translateY) / this.scaleY!,
      }
      this.canvas?.fire('object:modified', { target: this })
    })
  }

  private setDesignOptionEventHandlers(designOptionObject: fabric.Object, assetId: string) {
    const designObject = designOptionObject as WbDesignOption
    designOptionObject.on('modified', () => {
      const articleCoords = fabric.util.qrDecompose(this.calcTransformMatrix())
      const pos = this.designOptionData.find(p => p.id === assetId)
      if (!pos) { return }
      const designObjectCoords = fabric.util.qrDecompose(designOptionObject.calcTransformMatrix())
      // Convert from center-origin to top-left
      const topLeftX = designObjectCoords.translateX - (designOptionObject.width! * designOptionObject.scaleX!) / 2
      const topLeftY = designObjectCoords.translateY - (designOptionObject.height! * designOptionObject.scaleY!) / 2

      pos.link = {
        x: (topLeftX - articleCoords.translateX) / this.scaleX!,
        y: (topLeftY - articleCoords.translateY) / this.scaleY!,
      }
      this.canvas?.fire('object:modified', { target: this })
    })
    designObject.on('scaling', () => {
      const debouncedCheckAndReplace = debounce(() => {
        if (designObject.isUpdatingImage) { return }
        this.applyScaling(designOptionObject)
      }, 2000)
      if (!designObject.isUpdatingImage) {
        debouncedCheckAndReplace()
      }
    })
  }

  applyScaling(designOptionObject) {
    const userStore = useUserStore()
    const pos = this.designOptionData.find(p => p.id === designOptionObject.asset.Id)
    if (!pos) { return }
    const currentWidth = designOptionObject.width * designOptionObject.scaleX
    const currentHeight = designOptionObject.height * designOptionObject.scaleY
    const currentMaxSize = Math.max(currentWidth, currentHeight)
    const maxActualSize = Math.max(designOptionObject.asset.Width, designOptionObject.asset.Height)
    const aspectRatio = designOptionObject.asset.Width / designOptionObject.asset.Height

    const newResolution = WbDesignOption.getNearestResolution(currentMaxSize, maxActualSize)
    if (newResolution > Math.max(designOptionObject.width, designOptionObject.height)) {
      let newImageWidth = currentWidth
      let newImageHeight = Math.round(newResolution / aspectRatio)
      if (currentHeight > currentWidth) {
        newImageHeight = Math.round(currentHeight)
        newImageWidth = Math.round(newResolution / (currentHeight / currentWidth))
      }
      designOptionObject.isUpdatingImage = true
      const width = designOptionObject.asset.Height > designOptionObject.asset.Width ? newImageWidth : newResolution
      const height = designOptionObject.asset.Height > designOptionObject.asset.Width ? newResolution : newImageHeight
      const url = `${appConfig.AssetsUrl}/assets/content/${designOptionObject.assetStorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog!.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
      fabric.util.loadImage(url, (newImgEl) => {
        designOptionObject.setElement(newImgEl)
        designOptionObject.set({
          width,
          height,
          scaleX: currentWidth / width,
          scaleY: currentHeight / height,
        } as unknown as Partial<this>)
        pos.scale = (currentMaxSize / maxActualSize) || pos.scale
        designOptionObject.isUpdatingImage = false
        this.canvas?.requestRenderAll()
        this.canvas?.fire('object:modified', { target: this })
      }, null, 'anonymous')
    }
    else {
      pos.scale = (currentMaxSize / maxActualSize) || pos.scale
      this.canvas?.requestRenderAll()
      this.canvas?.fire('object:modified', { target: this })
    }
  }

  private applyNoImageProperties() {
    if (!this.assetKey) {
      this.set('borderColor', this.noImageObjectProperties.border && this.noImageObjectProperties.border.borderColor ? this.noImageObjectProperties.border.borderColor : '#000000')
      this.set('strokeWidth', this.noImageObjectProperties.border && this.noImageObjectProperties.border.strokeWidth ? this.noImageObjectProperties.border.strokeWidth : 1)
      // this.set('backgroundColor', this.noImageObjectProperties.backgroundColor ? this.noImageObjectProperties.backgroundColor : '#ffffff')
      if (this.noImageObjectProperties.attributes) {
        this.set('attributes', this.noImageObjectProperties.attributes)
      }
    }
  }

  async setProp(prop: string, value: any, ignoreHistory: boolean = false, notifyObjectModified: boolean = true) {
    switch (prop) {
      case 'imageType':
        this.set('assetKey', value.assetKey)
        this.imgLoaded = false
        this.imgLoading = false
        break
      case 'scale':
        if (value.scale <= 100 && this.width && this.height) {
          const scale = value.scale / 100
          this.set('scaleX', scale)
          this.set('scaleY', scale)
        }
        break
      case 'lock':
        this.set('lock', value.lock)
        this.setLock(value.lock)
        break
      case 'status':
        this.set('status', value)
        break
      case 'colorCode':
        this.set('colorCode', value)
        break
      case 'showResources':
        this.set('showResources', value.showResources)
        this.handleResourcesVisibility(Boolean(value.showResources))
        break
      case 'showDesignOptions':
        this.set('showDesignOptions', value.showDesignOptions)
        this.handleDesignOptionsVisibility(Boolean(value.showDesignOptions))
        break
      case 'attributes':
        this.noImageObjectProperties.attributes = value.attributes
        this.set('attributes', value.attributes)
        await this.loadTextValue()
        break
      case 'border':
        this.noImageObjectProperties.border = {
          borderColor: value.borderColor,
          strokeWidth: value.strokeWidth,
        }
        this.set('borderColor', value.borderColor)
        // this.set('borderScaleFactor', value.strokeWidth)
        break
      case 'backgroundColor':
        this.noImageObjectProperties.backgroundColor = value.backgroundColor
        // this.set('backgroundColor', value.backgroundColor)
        break
      case 'icons':
        this.set('icons', value.icons)
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    if (notifyObjectModified) {
      this.canvas?.fire('object:modified', { target: this, ignoreHistory })
    }
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'imageType':
        result.assetKey = this.assetKey
        break
      case 'scale':
        result.scale = Math.round((this.scaleX || 1) * 100)
        break
      case 'lock':
        result.lock = this.lock
        break
      case 'status':
        result.status = this.status
        break
      case 'showResources':
        result.showResources = this.showResources
        break
      case 'showDesignOptions':
        result.showDesignOptions = this.showDesignOptions
        break
      case 'attributes':
        result.attributes = this.assetKey ? [] : this.noImageObjectProperties.attributes
        break
      case 'backgroundColor':
        result.backgroundColor = this.assetKey ? 'transparent' : this.noImageObjectProperties.backgroundColor
        break
      case 'border':
        result.borderColor = this.assetKey ? this.borderColor : this.noImageObjectProperties.border?.borderColor
        result.strokeWidth = this.assetKey ? this.strokeWidth : this.noImageObjectProperties.border?.strokeWidth
        break
      case 'icons':
        result.icons = this.icons
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  highlight() {
    this.highlighted = true
    this.set('stroke', '#2196f3')
    this.set('strokeWidth', 4)
    this.set('strokeDashArray', [20])
    this.canvas?.requestRenderAll()
  }

  dim() {
    this.highlighted = false
    this.set('stroke', undefined)
    this.set('strokeWidth', 0)
    this.set('strokeDashArray', undefined)
    this.canvas?.requestRenderAll()
  }

  midIntersectsWithObject(other: fabric.Object, absolute?: boolean, calculate?: boolean) {
    const points1 = this.getCoords(absolute, calculate)
    const points2 = other.getCoords(absolute, calculate)

    const xDelta = (points1[1].x - points1[0].x) / 4
    const yDelta = (points1[2].y - points1[0].y) / 4
    const increaseInX = points1[0].x + xDelta
    const increaseInY = points1[0].y + yDelta

    for (const point of points1) {
      point.x = (point.x >= increaseInX) ? (point.x - xDelta) : (point.x + xDelta)
      point.y = (point.y >= increaseInY) ? (point.y - yDelta) : (point.y + yDelta)
    }

    const intersection = fabric.Intersection.intersectPolygonPolygon(points1, points2)

    return intersection.status === 'Intersection'
      || other.isContainedWithinObject(this, absolute, calculate)
      || this.isContainedWithinObject(other, absolute, calculate)
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'width',
      'height',
      'angle',
      'scaleX',
      'scaleY',
      'lock',
      'version',
      'preventUnlock',
      // 'backgroundColor',
      'borderColor',
      'catalogCode',
      'articleId',
      'objectId',
      'isRequest',
      'assetKey',
      'status',
      'localOnly',
      'stroke',
      'strokeWidth',
      'strokeDashArray',
      'noImageObjectProperties',
      'showResources',
      'showDesignOptions',
      'designOptionData',
      'resourceData',
      'isNewArticle',
      'icons',
    ]
    return pick(this, propsToPluck)
  }

  static fromObject(object: WbArticleImage, callback?: Function) {
    const userStore = useUserStore()
    // handle existing objects
    if (!object.catalogCode) { object.catalogCode = -1 }
    if (object.catalogCode && object.articleId) {
      this.loadArticleImageById(object.catalogCode, object.articleId, object.objectId, object.width || 500, object.height || 500, object).then((v) => {
        if (v && userStore.activeCatalog && ((!utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode]) || !utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].newArticleIndicator)) || userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].newArticleIndicator)) {
          v.checkAndMarkAsNew()
        }
        callback && callback(v)
      })
    }
  }

  static async getImageSrc(catalogCode: number, articleId: number, objectId: string, isRequest: boolean, width: number, height: number, blockImage: number, article?: MyArticle, assets?: DuneAsset[], assetKey?: string) {
    let src = pngNoImage
    const userStore = useUserStore()
    const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
    if (userStore && catalogDetails && assets && assets.length > 0 && (userStore.userProfile.isValidPrivilege(privileges.article.allowBlockedArticleImages) || blockImage === 0)) {
      let asset = assets[0]
      if (assetKey === 'firstImage') {
        const assetsClone = clone(assets)
        utils.sort(assetsClone, ['SortOrder'])
        asset = assetsClone[0]
      }
      else if (assetKey === 'newestImage') {
        asset = assets[0]
      }
      else {
        // if requested for specific assetKey then search in _Assets, if does not exist in _Assets it should show placeholder
        if (utils.isValidStringValue(assetKey)) {
          const selectedAsset = assets.find(asset => asset.Id === assetKey || asset.Key === assetKey)
          if (selectedAsset) {
            asset = selectedAsset
          }
        }
      }

      src = `${appConfig.AssetsUrl}/assets/content/${asset.StorageFile}?ContextKey=${encodeURIComponent(catalogDetails.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
    }
    else if (appConfig.DB && userStore.activeCatalog?.Config.SilhouetteImagesCriteria && !userStore.activeCatalog?.Config.ShowWhiteboardThumbnailAttributesIfNoImage) {
      let articleForSilhouette: Article | undefined
      if (isRequest) {
        const request = await appConfig.DB.requests.get({ CatalogCode: catalogDetails.CatalogCode, Id: articleId, IsCreateArticleRequest: 1 })
        if (request?.Content) {
          articleForSilhouette = await utils.getRequestArticle(catalogDetails, request, appConfig.DB, userStore.myAttributes, userStore.priceGroups)
        }
      }
      else if (article) {
        articleForSilhouette = article
      }
      else {
        const articles = await appConfig.DB.getArticlesByCatalogCodeArticleIds(userStore.activeCatalog, userStore.linkedCatalogDetails, [articleId], userStore.currentCustomer, userStore.myAttributes, [], true, userStore.currentCustomerSegmentations, true)
        if (articles && articles.length) {
          articleForSilhouette = articles[0]
        }
      }
      if (articleForSilhouette) {
        const imageKey = utils.getPlaceHolderImageKey(userStore.activeCatalog.Config.SilhouetteImagesCriteria, articleForSilhouette, userStore.indexedSilhouetteImages)
        if (utils.isValidStringValue(imageKey) && userStore.indexedSilhouetteImages[imageKey] && userStore.activeCatalog.ContextKey) {
          src = `${appConfig.AssetsUrl}/assets/content/${userStore.indexedSilhouetteImages[imageKey].StorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
        }
      }
    }
    return src
  }

  static loadArticleImage(article: MyArticle, width: number, height: number, opt?: IWbArticleImageOptions, favColorList?: string[]) {
    return new Promise<WbArticleImage>((resolve) => {
      fabric.util.loadImage(pngLoading, (img) => {
        const userStore = useUserStore()
        const catalogDetails = userStore.linkedCatalogDetails[article.CatalogCode] || userStore.activeCatalog
        let colorCode = ''
        if (userStore.activeCatalog?.Config.WhiteboardColorCodingAttribute && userStore.activeCatalog.Config.WhiteboardColorCodingAttribute.length !== 0 && userStore.activeCatalog.Config.WhiteboardColorCodingAttribute[0].attribute) {
          for (const configValue of userStore.activeCatalog.Config.WhiteboardColorCodingAttribute) {
            if (utils.isValidStringValue(article[configValue.attribute]) && article[configValue.attribute]?.toString().trim().toLowerCase() === configValue.value.toString().toLowerCase()) {
              colorCode = configValue.color
              break
            }
          }
        }

        const o: IWbArticleImageOptions = Object.assign((opt || { width, height }), {
          catalogCode: article.CatalogCode,
          articleId: article.Id,
          objectId: article.CatalogArticleId,
          isRequest: Boolean(article._IsRequestArticle),
          requestWaterMarkDetails: utils.getRequestWaterTextAndFontColor(article),
          colorCode,
          status: article.Status === 1,
          article,
          favColorList,
          articleWaterMarkDetails: catalogDetails.DataSourceTypeId === appConstants.catalogTypes.inherited && article.Status !== 1 ? utils.getArticleWaterTextAndFontColor(article) : {},
        })
        const obj = new WbArticleImage(img, o)
        obj.article = article
        obj.articleNumber = article.ArticleNumber
        obj.blockImage = article.BlockImage

        if (article._isAssetsLoaded) {
          obj.assets = article._Assets
          if (article._Assets.length && !utils.isDefined(obj.assetKey)) {
            obj.assetKey = article._Assets[0].Id
          }
        }
        else {
          obj.imgLoading = true
          getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, article.ArticleNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList, false, whiteboardConstants.abortController.signal)
            .then((assets) => {
              obj.assets = assets
              if (assets.length && !utils.isDefined(obj.assetKey)) {
                obj.assetKey = assets[0].Id
              }
            })
            .finally(() => {
              obj.imgLoaded = false
              obj.imgLoading = false
              obj.opacity = article.Status === 1 && !article._IsNonSegmented ? 1 : 0.5
              obj.dirty = true
              obj.canvas?.requestRenderAll()
              const activeObjects = obj.canvas?.getActiveObjects() as Array<IWbObject>
              if (activeObjects && activeObjects.length === 1 && activeObjects[0].id === obj.id) {
                obj.canvas?.discardActiveObject()
                obj.canvas?.setActiveObject(obj)
              }
            })
        }

        resolve(obj)
      })
    })
  }

  static async getJsonObject(article: MyArticle, width: number, height: number, opt?: IWbArticleImageOptions) {
    const userStore = useUserStore()
    const options = Object.assign((opt || { width, height, noImageObjectProperties: { backgroundColor: '#ffffff', border: { color: '#000000', strokeWidth: 1 }, attributes: userStore.activeCatalog?.Config.WhiteboardThumbnailAttributes.length ? userStore.activeCatalog?.Config.WhiteboardThumbnailAttributes : ['ArticleNumber', 'ModelName'] } }), { catalogCode: article.CatalogCode, articleId: article.Id, objectId: article.CatalogArticleId, isRequest: Boolean(article._IsRequestArticle), status: article.Status === 1 })
    const catalogDetails = userStore.linkedCatalogDetails[article.CatalogCode] || userStore.activeCatalog

    const obj: any = new WbArticleImage('', options, false).toObject()

    getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, article.ArticleNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList)
      .then((assets) => {
        obj.assets = assets
      })
      .finally(() => {
        obj.imgLoaded = false
        obj.imgLoading = false
        obj.opacity = article.Status === 1 && !article._IsNonSegmented ? 1 : 0.5
        obj.dirty = true
        obj.canvas?.requestRenderAll()
      })

    if (article._Assets && article._Assets.length > 0 && catalogDetails) {
      let asset = article._Assets[0]
      if (opt && opt.assetKey) {
        const articleAssets = await getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, article.ArticleNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList)

        if (opt && opt.assetKey === 'firstImage') {
          const assetsClone = clone(articleAssets)
          utils.sort(assetsClone, ['SortOrder'])
          asset = assetsClone[0]
        }
        else if (opt.assetKey === 'newestImage') {
          asset = article._Assets[0]
        }
        else {
          // if requested for specific assetKey then search in _Assets, if does not exist in _Assets it should show placeholder
          if (utils.isValidStringValue(opt.assetKey)) {
            const selectedAsset = articleAssets.find(asset => asset.Id === opt.assetKey || asset.Key === opt.assetKey)
            if (selectedAsset) {
              asset = selectedAsset
            }
          }
        }
      }
      obj.src = `${appConfig.AssetsUrl}/assets/content/${asset.StorageFile}?ContextKey=${encodeURIComponent(catalogDetails.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
    }
    else if (userStore.activeCatalog?.Config.SilhouetteImagesCriteria && !userStore.activeCatalog?.Config.ShowWhiteboardThumbnailAttributesIfNoImage) {
      if (options.noImageObjectProperties) {
        obj.text = await WbArticleImage.getText(article, options.noImageObjectProperties.attributes, '\n', '', userStore.myAttributes)
      }
      const imageKey = utils.getPlaceHolderImageKey(userStore.activeCatalog.Config.SilhouetteImagesCriteria, article, userStore.indexedSilhouetteImages)
      if (utils.isValidStringValue(imageKey) && userStore.indexedSilhouetteImages[imageKey] && userStore.activeCatalog.ContextKey) {
        obj.src = `${appConfig.AssetsUrl}/assets/content/${userStore.indexedSilhouetteImages[imageKey].StorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
      }
    }

    obj.height = height
    obj.width = width
    return obj
  }

  static async loadArticleImageById(catalogCode: number, articleId: number, objectId: string, width: number, height: number, opt: IWbArticleImageOptions) {
    const userStore = useUserStore()
    const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
    if (userStore.activeCatalog && appConfig.DB && userStore.myAttributes) {
      if (opt.isRequest) {
        const request = await appConfig.DB.requests.get({ CatalogCode: catalogDetails.CatalogCode, Id: articleId, IsCreateArticleRequest: 1 })
        if (request?.Content) {
          const article = await utils.getRequestArticle(catalogDetails, request, appConfig.DB, userStore.myAttributes, userStore.priceGroups)
          const retailPrice = userStore.priceGroups.retail ? article._Prices[userStore.priceGroups.retail.Id]?.Price : 0
          const wholesalePrice = userStore.priceGroups.wholesale ? article._Prices[userStore.priceGroups.wholesale.Id]?.Price : 0
          const outletPrice = userStore.priceGroups.outlet ? article._Prices[userStore.priceGroups.outlet.Id]?.Price : 0
          const datasetAttributes = Object.values(userStore.myAttributes!).filter(attr => attr.IsDatasetAttribute)
          const myArticle = new MyArticle(article, catalogDetails.AssignedCatalogAttributes, retailPrice, wholesalePrice, outletPrice, [], true, [], [], [], catalogDetails.Season, catalogDetails.DataSourceTypeId, catalogDetails.Config.ArticlesCustomSortOrder, datasetAttributes)
          return await this.loadArticleImage(myArticle, width, height, opt)
        }
      }
      else if (opt.article) {
        return await this.loadArticleImage(opt.article, width, height, opt, opt.article._FavoriteTags.map(tag => tag.Color))
      }
      else {
        const filters: ArticleFilterModel[] = []
        if (Object.keys(userStore.linkedCatalogDetails).length > 0) {
          const linkedCatalogCodes: number[] = []
          for (const linkedCatalogCode of Object.keys(userStore.linkedCatalogDetails)) {
            linkedCatalogCodes.push(Number(linkedCatalogCode))
          }
          filters.push({ attribute: '_Seasons', mode: FilterCriteriaMode.multiString, value: linkedCatalogCodes, exclude: false })
        }
        const responseArticles = await getArticlesStream(userStore.activeCatalog.CatalogCode, { articleId, body: { filters } }, whiteboardConstants.abortController.signal)

        let responseArticle = responseArticles.find(x => x.CatalogCode === catalogDetails.CatalogCode && x.Id === articleId)
        if (!responseArticle) {
          responseArticle = responseArticles.find(x => x.Id === articleId)
        }
        if (responseArticle) {
          const myArticles = await appConfig.DB.convertToMyArticles(userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes, userStore.currentUsername, userStore.userProfile.Id, [responseArticle], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, userStore.currentCustomerSegmentations, true)
          if (myArticles.length) {
            return await this.loadArticleImage(myArticles[0], width, height, opt, myArticles[0]._FavoriteTags.map(tag => tag.Color))
          }
        }
      }
      console.warn('Failed to load article image by Id')
    }
    console.warn('Unable to load Article Image by Id')
    return null
  }

  static async getText(article: MyArticle, attributes?: string[], attributesSeparator?: string, attributePlaceHolder?: string, myAttributes?: Record<string, IMyAttribute>) {
    const attributeData: any[] = []
    const userStore = useUserStore()
    if (attributes && attributes.length && utils.isDefined(myAttributes)) {
      for (const attributeSystemName of attributes) {
        if (utils.isDefined(myAttributes[attributeSystemName])) {
          let val = await utils.getAttributeTypeSpecificValue(myAttributes[attributeSystemName], article, appConfig.DB, userStore.activeCatalog, userStore.priceGroups, false, true)
          if (!utils.isValidStringValue(val)) {
            val = attributePlaceHolder
          }
          attributeData.push(val)
        }
      }
    }
    return attributeData.join(attributesSeparator)
  }

  async loadTextValue(article?: MyArticle) {
    if (!this.noImageObjectProperties?.attributes) { return }

    const userStore = useUserStore()
    let articleData = article || this.article
    if (!articleData) {
      const articles = await appConfig.DB!.getMyArticlesByIds(userStore.activeCatalog!, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, [this.articleId], userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [], userStore.currentCustomerSegmentations, true)
      if (articles.length) {
        articleData = articles[0]
      }
    }
    if (articleData) {
      this.cachedAttributeThumbnailValue = await WbArticleImage.getText(articleData, this.noImageObjectProperties.attributes, '\n', '', userStore.myAttributes)
    }
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    super._render(ctx)
    const userStore = useUserStore()
    const userWhiteboardSettings = utils.isDefined(userStore.settingsContainer) && userStore.settingsContainer.whiteboard ? userStore.settingsContainer.whiteboard : ''
    if (!this.imgLoaded && !this.imgLoading) {
      console.log('loading image')
      this.imgLoading = true
      WbArticleImage.getImageSrc(this.catalogCode, this.articleId, this.objectId, this.isRequest, 500, 500, this.blockImage || 0, this.article, this.assets, this.assetKey)
        .then((src) => {
          if (src !== pngNoImage) {
            promiseQueue.add(() => new Promise((resolve, reject) => this.setSrc(src, (img, error) => error ? reject(error) : resolve(img), { crossOrigin: 'anonymous' }))
              .then(() => {
                this.displayedImg = 'image'
                this.clearNoImageProperties()
                this.canvas?.requestRenderAll()
              }).catch((error) => {
                this.setSrc(pngNoImage, () => this.canvas?.requestRenderAll())
                this.displayedImg = 'noImage'
                this.imgLoaded = true
                console.warn('unable to load WbArticleImage', this, error)
              }).finally(() => {
                this.imgLoaded = true
                this.imgLoading = false
              }),
            )
          }
          else {
            this.setSrc(pngNoImage, () => this.canvas?.requestRenderAll())
            this.displayedImg = 'noImage'
            this.imgLoaded = true
            this.imgLoading = false
          }
        })
    }

    if ((userStore.activeCatalog?.Config.ShowWhiteboardThumbnailAttributesIfNoImage && (this.displayedImg === 'noImage' || this.displayedImg === 'articleDetails')) && !this.isRequest) {
      this.imgLoading = false
      this.editableProps.addThumbnailAttributes.editable = true
      this.editableProps.backgroundColor.editable = true
      this.editableProps.border.editable = true
      this.editableProps.scale.editable = false

      ctx.fillStyle = 'transparent'
      ctx.fillRect(-this.width! / 2, -this.height! / 2, this.width!, this.height!)

      // image is deactivated from details
      if (isEmpty(this.noImageObjectProperties)) {
        this.noImageObjectProperties.border = { borderColor: '#000000', strokeWidth: 1 }
        this.noImageObjectProperties.attributes = userStore.activeCatalog?.Config.WhiteboardThumbnailAttributes.length ? userStore.activeCatalog?.Config.WhiteboardThumbnailAttributes : ['ArticleNumber', 'ModelName'] // default are ArticleNumber and ModelName // assign default
      }

      if (this.noImageObjectProperties.border) {
        if (!this.cachedAttributeThumbnailValue) {
          this.loadTextValue()
        }

        const borderColor = this.noImageObjectProperties.border.borderColor || '#000000'
        const borderWidth = this.noImageObjectProperties.border.strokeWidth || 5

        ctx.beginPath()
        ctx.fillStyle = this.noImageObjectProperties.backgroundColor || (this.cachedAttributeThumbnailValue && this.cachedAttributeThumbnailValue.length ? '#ffffff' : 'transparent')
        ctx.lineWidth = borderWidth
        ctx.roundRect(
          -this.width! / 2,
          -this.height! / 2,
          this.width!,
          this.height!,
          15,
        )
        // ctx.lineWidth = scaleValue
        ctx.strokeStyle = borderColor
        ctx.stroke()
        ctx.fill()

        const fontFamily = (userWhiteboardSettings !== '' && userWhiteboardSettings.articleNoImageFont && userWhiteboardSettings.articleNoImageFont !== '')
          ? userWhiteboardSettings.articleNoImageFont
          : utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.whiteboardDefaultFont) && appConfig.dynamicConfig.whiteboardDefaultFont.length
            ? appConfig.dynamicConfig.whiteboardDefaultFont
            : 'Helvetica'

        ctx.fillStyle = '#000'
        ctx.font = `bold 30px ${fontFamily}`
        ctx.textAlign = 'center'

        if (this.cachedAttributeThumbnailValue && this.cachedAttributeThumbnailValue.length) {
          const wrappedLines = utils.getWrappedText(ctx, this.cachedAttributeThumbnailValue, this.width)
          // const attributeValueArray = this.cachedAttributeThumbnailValue.split('\n')
          const totalLines = wrappedLines.length
          const lineHeight = 35
          const centerY = 0
          let startY = 0
          if (this.width && ((totalLines * lineHeight) >= this.width)) {
            startY = centerY - (this.width / 2)
          }
          else {
            startY = centerY - ((totalLines - 1) / 2) * lineHeight
          }

          wrappedLines.forEach((text, index) => {
            ctx.fillText(text, 0, startY + index * lineHeight)
          })
          this.displayedImg = 'articleDetails'
        }
      }
    }
    else {
      // hide noImageProperies
      this.editableProps.addThumbnailAttributes.editable = false
      this.editableProps.backgroundColor.editable = false
      this.editableProps.border.editable = false
      this.editableProps.scale.editable = true
    }

    if (this.selected) {
      if (this.articleNumber) {
        // const userStore = useUserStore()
        ctx.font = '50px Helvetica'
        ctx.textAlign = 'left'
        const measure = ctx.measureText(this.articleNumber)
        // let seasonData = userStore.activeCatalog?.CatalogCode === this.article.CatalogCode
        //   ? userStore.activeCatalog.Season
        //   : userStore.linkedCatalogDetails[this.article.CatalogCode].Season
        // const seasonDataMeasure = ctx.measureText(seasonData)

        ctx.fillStyle = '#1484f8'
        const width = this.width || 0
        const height = this.height || 0
        ctx.fillRect(-width / 2, -height / 2, measure.width, 60)
        // ctx.fillRect(this.width! / 2 - seasonDataMeasure.width, -this.height! / 2, seasonDataMeasure.width, 60)
        ctx.fillStyle = '#FFFFFF'
        ctx.fillText(this.articleNumber, -width / 2, -height / 2 + 50)
      }
      // ctx.fillText(seasonData, this.width! / 2 - seasonDataMeasure.width, -this.height! / 2 + 50)
    }

    if (this.isNewArticle && userStore.activeCatalog && ((!utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode]) || !utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].newArticleIndicator)) || userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].newArticleIndicator)) {
      ctx.save()
      const labelText = 'New'
      ctx.font = '50px Helvetica'
      ctx.textAlign = 'right'
      const measure = ctx.measureText(labelText)

      ctx.fillStyle = '#1484f8'
      const width = this.width || 0
      const height = this.height || 0
      ctx.fillRect(width / 2 - measure.width, -height / 2, measure.width, 60)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillText(labelText, width / 2, -height / 2 + 50)
      ctx.restore()
    }

    if (this.icons.length > 0) {
      this.renderIcons(ctx)
    }

    if (this.isRequest) {
      const fontColor = this.requestWaterMarkDetails.fontColor || '#5093e16e'
      const waterMarkText = this.requestWaterMarkDetails.waterMarkText || '#5093e16e'
      const text = waterMarkText.split(' ')
      ctx.lineWidth = 1
      ctx.fillStyle = fontColor
      ctx.font = '50px arial'
      ctx.rotate(-Math.PI / 4)
      ctx.textAlign = 'center'
      if (text.length === 1) {
        ctx.fillText(text, 0, 0)
      }
      else if (text.length === 2) {
        let y = 0
        text.forEach((value, index) => {
          if (index === 0) {
            y = -50
          }
          else if (index === 1) {
            y = 50
          }
          ctx.fillText(value, 0, y)
        })
      }
      else if (text.length === 3) {
        text.forEach((value, index) => {
          let y = 0
          if (index === 0) {
            y = -100
          }
          else if (index === 2) {
            y = 100
          }
          ctx.fillText(value, 0, y)
        })
      }
      else if (text.length === 4) {
        text.forEach((value, index) => {
          let y = 0
          if (index === 0) {
            y = -100
          }
          else if (index === 1) {
            y = -35
          }
          else if (index === 2) {
            y = 35
          }
          else if (index === 3) {
            y = 100
          }
          ctx.fillText(value, 0, y)
        })
      }
      else {
        ctx.fillText(text, 0, 0)
      }
    }
    else {
      // if non-assorted article/globbay dropped
      if (!isEmpty(this.articleWaterMarkDetails)) {
        const fontColor = this.articleWaterMarkDetails.fontColor
        const waterMarkText = this.articleWaterMarkDetails.waterMarkText
        ctx.lineWidth = 1
        ctx.fillStyle = fontColor
        ctx.font = 'bold 50px arial'
        ctx.rotate(-Math.PI / 4)
        ctx.textAlign = 'center'
        ctx.fillText(waterMarkText, 0, 0)
      }
      else if (!this.status || (this.article && this.article._IsNonSegmented)) {
        ctx.lineWidth = 8
        ctx.beginPath()
        ctx.moveTo(-((this.width || 500) / 2), -((this.height || 500) / 2))
        ctx.lineTo((this.width || 500) / 2, (this.height || 500) / 2)
        ctx.strokeStyle = 'black'
        ctx.stroke()
        ctx.lineWidth = 5
        ctx.strokeStyle = 'red'
        ctx.stroke()
        ctx.lineWidth = 8
        ctx.beginPath()
        ctx.moveTo(((this.width || 500) / 2), -((this.height || 500) / 2))
        ctx.lineTo(-((this.width || 500) / 2), (this.height || 500) / 2)
        ctx.strokeStyle = 'black'
        ctx.stroke()
        ctx.lineWidth = 5
        ctx.strokeStyle = 'red'
        ctx.stroke()
      }
      const centerY = -(this.height || 500) / 2 + 20
      if (this.colorCode !== '') {
        const radius = 18
        const centerX = ((this.width || 500) / 2) - 20

        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, 0, (2 * Math.PI), false)
        ctx.fillStyle = this.colorCode
        ctx.fill()
      }
      if (userStore.activeCatalog && ((!utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode]) || !utils.isDefined(userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showFavorites)) || userStore.userPreferences.whiteboard.settings[userStore.activeCatalog.CatalogCode].showFavorites) && this.favColorList && this.favColorList.length) {
        const radius = 12
        for (let index = 0; index < this.favColorList.length; index++) {
          const centerX = index % 2 === 0 ? (index / 2 * 32) : -(index / 2 * 32) - 14
          ctx.beginPath()
          ctx.arc(centerX, centerY, radius, 0, (2 * Math.PI), false)
          ctx.fillStyle = this.favColorList[index]
          ctx.fill()
          if (index === 14) {
            break
          }
        }
      }
    }
  }

  private renderIcons(ctx: CanvasRenderingContext2D) {
    if (this.icons.length === 0) { return }
    const iconsToShow = this.icons
    const availableIcons = whiteboardConstants.availableIcons.map(icon => icon.name)
    const baseIconSize = 35
    const scaleFactor = Math.min(this.scaleX || 1, this.scaleY || 1)
    const iconSize = Math.max(16, baseIconSize * scaleFactor)
    const padding = 8 * scaleFactor
    const iconSpacing = 4 * scaleFactor
    const totalIconsWidth = (iconsToShow.length * iconSize) + ((iconsToShow.length - 1) * iconSpacing)
    const startX = (this.width || 0) - padding - totalIconsWidth
    const startY = (this.height || 0) - padding - iconSize
    ctx.save()
    ctx.translate(-this.width! / 2, -this.height! / 2)
    iconsToShow.forEach((iconName, index) => {
      if (!availableIcons.includes(iconName)) { return }
      const iconX = startX + (index * (iconSize + iconSpacing))
      const iconY = startY
      Utility.drawIcon(ctx, iconName, iconX, iconY, iconSize)
    })
    ctx.restore()
  }

  clearNoImageProperties() {
    this.noImageObjectProperties = {}
    this.set('backgroundColor', 'transparent')
    this.set('borderColor', undefined)
    this.set('strokeWidth', 0)
    this.set('borderScaleFactor', 0)
    this.set('strokeDashArray', undefined)
    this.set('attributes', [])
  }

  private checkAndMarkAsNew() {
    const userStore = useUserStore()
    const whiteboardNewArticleIndicationHours = userStore.activeCatalog?.Config.WhiteboardNewArticleIndicationHours || 24

    if (!this.article?.CreatedDate) {
      this.isNewArticle = false
      return
    }

    const createdDateLocal = this.article.CreatedDate // already converted to new local date
    const now = new Date()
    const hoursDiff = (now.getTime() - createdDateLocal.getTime()) / (1000 * 60 * 60) // convert to hours

    this.isNewArticle = hoursDiff <= whiteboardNewArticleIndicationHours

    if (this.isNewArticle) {
      const hoursRemaining = whiteboardNewArticleIndicationHours - hoursDiff
      const millisecondsRemaining = hoursRemaining * 60 * 60 * 1000

      if (this.newArticleTimeout) {
        clearTimeout(this.newArticleTimeout)
      }

      this.newArticleTimeout = window.setTimeout(() => {
        this.isNewArticle = false
        this.canvas?.requestRenderAll()
      }, millisecondsRemaining)
    }

    this.canvas?.requestRenderAll()
  }

  public cleanupNewArticleTimeouts() {
    if (this.newArticleTimeout) {
      clearTimeout(this.newArticleTimeout)
      this.newArticleTimeout = null
    }
  }
}

const f: any = fabric
f.WbArticleImage = WbArticleImage
